import React, { useContext, useState, useRef } from 'react'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { IContextMenuState, IFile, IFileParentResource } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import api from 'helpers/api'
import FileViewer, { formatFileSize } from 'components/file-viewer'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { AttachedFile, Cross, Fullscreen, Plus, Upload } from 'components/icons'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import Spinner from 'components/spinner'
import HoverCard from 'components/hover-card'
import Tooltip from 'components/tooltip'

interface AttachmentCellProps {
  value: IFile[]
  align: 'center' | 'left' | 'right'
  color: string
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  rowHeight: number
  selected: boolean
  columnId: string | undefined
  isExpanded?: boolean
  /** In a form: the files listed one to a row, each to view or remove, over an area to drop more on or choose them from. */
  asFormField?: boolean
  /** The column's name, which names the field in a form. */
  label?: string
  setCellValue: (value: IFile[], onSuccess: () => void, onError: () => void) => void
}

const styles = cellStyles()

// How many files a cell holds, as its hover card's title and a count: "1 file", "3 files".
const fileCount = (count: number): string => (count === 1 ? '1 file' : `${count} files`)
// The most files a cell's hover card lists by name.
const HOVER_FILES = 8

const AttachmentCellComponent: React.FC<AttachmentCellProps> = ({
  value,
  align,
  color,
  readOnly,
  isAdmin,
  isContributor,
  rowHeight,
  selected,
  isExpanded,
  setCellValue,
  columnId,
  asFormField,
  label
}) => {
  // In a form, named as the field it's drawn in.
  const labelledBy = useContext(FormFieldLabel)
  const inputRef = useRef<HTMLDivElement>(null)
  const { spreadsheetData } = useDataContext()
  const attachmentUploaderInputRef = useRef<HTMLInputElement>(null)
  const [attachmentMenu, setAttachmentMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [processing, setProcessing] = useState<boolean>(false)
  // hideInput allows to (re)render a input element every time a file is uploaded.
  // This allows the onchange event to be fired in the case a user deletes a file and chooses to upload the same file again.
  const [hideInput, setHideInput] = useState<boolean>(false)
  const [attachmentModal, setAttachmentModal] = useState<boolean>(false)
  // In a form: the file to open the viewer on, and whether files are being dragged over the drop area.
  const [viewing, setViewing] = useState<number>(0)
  const [dragging, setDragging] = useState<boolean>(false)

  const uploadFile = async (file: File) => {
    const resources: IFileParentResource[] = [
      { resource: 'document_table', publicId: spreadsheetData!.tableDetails.publicId }
    ]

    try {
      const urlData = await api.uploadFile(file, resources)
      if (urlData) {
        return urlData.data
      } else {
        // The batch carries on with the other files, and releases the field once their value is saved.
        setSnackbarMessage({
          status: 'error',
          message: "Your file couldn't be uploaded. Try again.",
          offerSupport: true
        })
        return null
      }
    } catch (error) {
      displayErrorMessage(error)
      return null
    }
  }

  // Held as soon as a change to the list starts, so a second drop or removal in the same moment waits, though the
  // state that disables the controls hasn't rendered yet.
  const busy = useRef(false)
  const hold = (): boolean => {
    if (busy.current) return false
    busy.current = true
    setProcessing(true)
    return true
  }
  const release = () => {
    busy.current = false
    setProcessing(false)
  }

  const addFiles = async (uploadedFiles: FileList | null) => {
    if (!uploadedFiles || uploadedFiles.length === 0 || !hold()) return
    const allFiles = value ? [...value] : []
    setHideInput(true)
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i]
      const newFileUrl = await uploadFile(file)
      if (newFileUrl) allFiles.push(newFileUrl)
    }
    setHideInput(false)
    setCellValue(allFiles, release, release)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => addFiles(event.target.files)

  const canAddLinks = isAdmin || isContributor

  const canChange = !readOnly && canAddLinks
  const chooseFiles = () => {
    if (!processing) attachmentUploaderInputRef.current?.click()
  }
  const files = Array.isArray(value) ? value.filter((file) => file !== null) : []
  // One change to the list at a time: a removal is built from the list as it's shown, so another before it's
  // saved would write a stale list back. Uploads and removals hold `processing` until the value is saved.
  const removeFile = (index: number) => {
    if (!hold()) return
    setCellValue(
      files.filter((_, at) => at !== index),
      release,
      release
    )
  }

  const viewer = (
    <FileViewer
      files={files}
      open={attachmentModal}
      onOpenChange={setAttachmentModal}
      initialIndex={viewing}
      tableId={spreadsheetData?.tableDetails.publicId}
      columnId={columnId}
      // Not while the list is changing (an upload or another removal being saved): either saves the list as it
      // was when it began, so a file removed meanwhile would come back.
      onRemove={canChange && !processing ? removeFile : undefined}
      // A form adds files from its drop area, under the list.
      onAdd={canChange && !asFormField ? chooseFiles : undefined}
    />
  )

  if (asFormField) {
    return (
      <div className="cell-value flex w-full flex-col gap-2" data-focussed={selected || undefined}>
        {files.length > 0 && (
          <ul
            aria-label={label}
            className="m-0 list-none divide-y divide-divider rounded-lg border border-divider bg-field p-0"
          >
            {files.map((file, index) => (
              <li key={file.publicId ?? index} className="flex h-11 min-w-0 items-center gap-2.5 pl-3 pr-1.5">
                <AttachedFile className="shrink-0 text-muted" />
                <span className="min-w-0 flex-1 truncate">{file.filename}</span>
                <span className="shrink-0 text-muted tabular-nums">{formatFileSize(file.filesize)}</span>
                <span className="flex shrink-0 items-center">
                  <Button
                    internalType="transparent"
                    className="size-7 p-0"
                    aria-label={`View ${file.filename}`}
                    onClick={() => {
                      setViewing(index)
                      setAttachmentModal(true)
                    }}
                  >
                    <Fullscreen />
                  </Button>
                  {canChange && (
                    <Button
                      internalType="transparent"
                      className="size-7 p-0"
                      aria-label={`Remove ${file.filename}`}
                      disabled={processing}
                      onClick={() => removeFile(index)}
                    >
                      <Cross />
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {canChange && (
          // Files dropped here or chosen from the button are added to those above. A click anywhere on it
          // chooses files too; the button is there for the keyboard.
          <div
            data-dragging={dragging || undefined}
            className={[
              'flex min-h-[112px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-lg',
              'border border-dashed border-divider bg-field px-4 py-6 text-center text-muted transition-colors',
              'hover:border-faint data-dragging:border-blue data-dragging:bg-blue/5'
            ].join(' ')}
            onClick={chooseFiles}
            // Only files from the computer: a field being dragged to reorder the form passes over it.
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes('Files')) return
              event.preventDefault()
              event.stopPropagation()
              if (!dragging) setDragging(true)
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
            }}
            onDrop={(event) => {
              if (!event.dataTransfer.types.includes('Files')) return
              event.preventDefault()
              event.stopPropagation()
              setDragging(false)
              addFiles(event.dataTransfer.files)
            }}
          >
            {processing ? (
              <>
                <span className="flex size-8 items-center justify-center rounded-md border border-divider bg-surface shadow-xs">
                  <Spinner className="size-4" />
                </span>
                <span>Uploading…</span>
              </>
            ) : (
              <>
                {/* The icon on a tile of its own, as an empty state's is. */}
                <span className="flex size-8 items-center justify-center rounded-md border border-divider bg-surface text-body shadow-xs">
                  <Upload />
                </span>
                <span>
                  Drop files here, or{' '}
                  <Button
                    internalType="transparent"
                    // "choose files", for the field it's in.
                    aria-describedby={labelledBy}
                    className="h-auto p-0 font-medium text-body underline-offset-2 hover:bg-transparent hover:underline"
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation()
                      chooseFiles()
                    }}
                  >
                    choose files
                  </Button>
                </span>
              </>
            )}
          </div>
        )}
        {!canChange && files.length === 0 && (
          <div className="flex h-9 items-center rounded-lg border border-divider bg-field px-3 text-faint">
            No files
          </div>
        )}
        {!hideInput && (
          <input
            type="file"
            ref={attachmentUploaderInputRef}
            onChange={handleFileChange}
            className="hidden"
            multiple
            aria-labelledby={labelledBy}
          />
        )}
        {viewer}
      </div>
    )
  }

  return (
    <div
      className={styles.value({ focused: selected, class: 'group/attachment relative cell-value' })}
      data-focussed={selected || undefined}
      ref={inputRef}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        maxHeight: `${getRowHeightVariable(rowHeight) - 3}px`,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)'
      }}
      onContextMenu={() =>
        setAttachmentMenu({
          ...attachmentMenu,
          open: false
        })
      }
      onClick={(event: React.MouseEvent) => {
        event.persist()
        const { left, bottom } = event.currentTarget.getBoundingClientRect()
        setAttachmentMenu({
          ...attachmentMenu,
          open: true,
          top: `${bottom}px`,
          left: `${left}px`,
          bottom: 'auto',
          right: 'auto'
        })
      }}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      {/* A + the size of a checkbox to add files (on the selected cell), then how many there are as a link that
          opens them in the viewer: "1 file", "3 files". */}
      {/* No wider than the cell (less its padding): the cell lays out as a table cell, which would grow to fit a long
          name rather than cut it short. */}
      <div className="flex h-full min-w-0 max-w-[calc(var(--cell-width)-16px)] items-center gap-1.5">
        {canChange && (
          // On the selected cell, and on any cell the pointer is over, so files can be added without opening them.
          <Tooltip content="Add files">
            <Button
              internalType="transparent"
              aria-label={label ? `Add files to ${label}` : 'Add files'}
              className={[
                'size-4 shrink-0 rounded-[4px] border border-divider bg-field p-0 text-muted',
                'hover:border-faint hover:bg-field hover:text-body [&_svg]:size-3',
                selected || isExpanded || processing ? '' : 'hidden group-hover/attachment:inline-flex'
              ].join(' ')}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                chooseFiles()
              }}
            >
              {processing ? <Spinner className="size-3" /> : <Plus />}
            </Button>
          </Tooltip>
        )}
        {!hideInput && (
          <input type="file" ref={attachmentUploaderInputRef} onChange={handleFileChange} className="hidden" multiple />
        )}
        {files.length > 0 && (
          // As the audit log's "1 change": underlined in the text's colour, with a card on hover listing the files.
          // A single file is named; more are counted.
          <HoverCard
            plain
            className="flex min-w-0"
            title={fileCount(files.length)}
            content={
              <div className="flex flex-col gap-1.5">
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-body">
                  {files.slice(0, HOVER_FILES).map((file, index) => (
                    <li key={file.publicId ?? index} className="flex min-w-0 gap-2">
                      {/* The underline on the text itself, with room under it, so cutting a long name short doesn't clip it. */}
                      <span className="min-w-0 truncate leading-5 underline decoration-body/40 underline-offset-2 group-hover:decoration-body">
                        {file.filename}
                      </span>
                      <span className="ml-auto shrink-0 text-muted tabular-nums">{formatFileSize(file.filesize)}</span>
                    </li>
                  ))}
                  {files.length > HOVER_FILES && <li className="text-muted">and {files.length - HOVER_FILES} more</li>}
                </ul>
                <span className="text-[12px] text-faint">Click to preview</span>
              </div>
            }
          >
            <Button
              internalType="transparent"
              className={[
                'group h-auto min-w-0 shrink p-0 font-normal text-body hover:bg-transparent hover:text-body'
              ].join(' ')}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                setViewing(0)
                setAttachmentModal(true)
              }}
            >
              {/* The underline on the text itself, with room under it, so cutting a long name short doesn't clip it. */}
              <span className="min-w-0 truncate leading-5 underline decoration-body/40 underline-offset-2 group-hover:decoration-body">
                {files.length === 1 ? files[0].filename : fileCount(files.length)}
              </span>
            </Button>
          </HoverCard>
        )}
      </div>
      {viewer}
    </div>
  )
}

export const AttachmentCell = AttachmentCellComponent
